declare const WEBFLOW_API_HOST: string | undefined;
// wfenvironment is rendered on the page by the server with environment specific variables.
declare type WfEnvironmentType = {
  [key: string]: boolean | string | undefined | null | number;
};

// @ts-expect-error - TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | { document: any; navigator: any; wfenvironment: any; }'. | TS2525 - Initializer provides no value for this binding element and the binding element has no default value.
const {
  document,
  navigator,
  wfenvironment: WF_ENVIRONMENT,
}: Window &
  typeof globalThis & {wfenvironment: WfEnvironmentType} = typeof window !==
'undefined'
  ? window
  : {};

import {DESIGNER_WORKFLOWS} from '@packages/systems/designer/workflows';
import {has} from '@packages/utilities/key-map';
import {parse as parseQueryString} from 'qs';
import get from 'lodash/get';

/**
 * Get string values from the global environment rendered to the page.
 */
export function getWfEnvironmentString(key: string): string {
  const result =
    typeof WF_ENVIRONMENT !== 'undefined' ? get(WF_ENVIRONMENT, key, '') : '';
  return typeof result === 'string' ? result : '';
}

// TODO: move these somewhere less hidden.
export const MIN_CHROME_VERSION = 59; // Cypress is chrome 59 so...
export const MIN_SAFARI_VERSION = 10.1;

const userAgent =
  typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
const platform = typeof navigator !== 'undefined' ? navigator.platform : '';

// MS Edge masquerades as Chrome+Safari+Mozilla, so we have to detect it first
const isMsEdge = /edge\//.test(userAgent);
const isChrome =
  typeof window !== 'undefined' &&
  !isMsEdge &&
  (window.chrome || /chrome\//.test(userAgent));
const isFirefox =
  typeof window !== 'undefined' &&
  !isMsEdge &&
  !isChrome &&
  userAgent.indexOf('firefox') >= 0;

const isSafari9OrBelow =
  isMsEdge &&
  !isFirefox &&
  !isChrome &&
  typeof window !== 'undefined' &&
  Object.prototype.toString.call(window.HTMLElement).indexOf('Constructor') > 0;
const isSafariTechPreview =
  !isMsEdge && !isChrome && userAgent.indexOf('safari') >= 0;
const isSafari = isSafari9OrBelow || isSafariTechPreview;

export const isSafariVersion17_2OrGreater = () => safariVersion >= 17.2;

type Browser =
  | 'Mozilla Firefox'
  | 'Microsoft Edge'
  | 'Google Chrome'
  | 'Apple Safari'
  | 'Unknown Browser';
// prettier-ignore
// The order matters here, and this may report false positives for unlisted browsers.
export const probablyBrowser: Browser = (
  isFirefox ? 'Mozilla Firefox' :
  isMsEdge ? 'Microsoft Edge' :
  isChrome ? 'Google Chrome' :
  (isSafariTechPreview || isSafari) ? 'Apple Safari' :
  'Unknown Browser'
);

const chromeVersion = (() => {
  const versionMatch = userAgent.match(/chrome\/(\d+)\./);
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  return isChrome && versionMatch ? parseInt(versionMatch[1], 10) : -1;
})();

const maybeSafariVersions = userAgent.match(/version\/(\d+\.*\d*)/);
const safariVersion =
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  isSafari && maybeSafariVersions ? parseFloat(maybeSafariVersions[1]) : -1;

const hostname =
  typeof window !== 'undefined' ? window.location.hostname || '' : '';

const getQuery = () => {
  return typeof window !== 'undefined'
    ? parseQueryString(window.location.search.replace(/^\?/, ''))
    : {};
};

export const query: any = getQuery();

// Global environment flags
export const OSX: boolean =
  /mac os x/.test(userAgent) || platform === 'MacIntel';
export const IOS: boolean = /iPad|iPhone|iPod/.test(platform);
export const WINDOWS = platform.indexOf('Win') === 0;
// Chrome adds `android` to it’s UA when devtools Responsive View is toggled.
// so we check to ensure it’s not on OSX or Windows as well
export const ANDROID: boolean =
  userAgent.indexOf('android') >= 0 && !OSX && !WINDOWS;
export const CHROME: boolean = isChrome && chromeVersion >= MIN_CHROME_VERSION;
export const SAFARI: boolean = isSafari && safariVersion >= MIN_SAFARI_VERSION;
export const FIREFOX: boolean = isFirefox;

// Set to true when disableBlockers query is not true and:
//  * A site is opened via a read-only preview link
//  * A saved backup version is being previewed
export const READ_ONLY: any | boolean =
  (hostname.indexOf('preview.') === 0 ||
    Boolean(query.preview) ||
    Boolean(query.p)) &&
  !query.disableBlockers;

export const ANONYMOUS_COMMENT: any | boolean =
  (hostname.indexOf('comments.') === 0 || Boolean(query.project)) &&
  !query.disableBlockers;

export const PREVIEW_ID = query.preview;
export const PROJECT_ID = query.project;

// Dynamically look up scrollbar visibility + width details
// In order to do this, we need to create two temporary divs - one to gauge whether scrollbars are visible
// and another to determine the width of the custom kit-scrollbar styling. If we only use one probe (e.g. with kit-scrollbar applied),
// then Chrome automatically shows the scrollbar even if the system settings are set up to only show when scrolling.
const _scrollbarDetails: {
  visible: boolean;
  width: number;
} = (function () {
  if (!document) {
    return {visible: false, width: 0};
  }

  const body = document.body;
  if (!body) {
    return {visible: false, width: 0};
  }

  const visibilityProbe = document.createElement('div');
  const widthProbe = document.createElement('div');
  const probes = [visibilityProbe, widthProbe];
  let scrollbarVisible = false;
  let scrollbarWidth = 0;

  probes.forEach((probe) => {
    const style = probe.style;

    style.position = 'absolute';
    style.width = '100px';
    style.height = '100px';
    style.overflow = 'scroll';
    style.top = '-9999px';

    if (probe === widthProbe) {
      probe.className = 'kit-scrollbar';
    }

    body.appendChild(probe);

    const scrollWidth = probe.offsetWidth - probe.clientWidth;
    if (probe === visibilityProbe && scrollWidth > 0) {
      scrollbarVisible = true;
    } else if (probe === widthProbe) {
      scrollbarWidth = scrollWidth;
    }

    body.removeChild(probe);
  });

  return {
    visible: scrollbarVisible,
    width: scrollbarWidth,
  };
})();

// Indicates the width of the kit-scrollbar
export const SCROLLBAR_WIDTH = _scrollbarDetails.width;

// Indicates whether the user's environment has scrollbars enabled when content overflows (true for Windows and some OSX configurations)
// To enable this in OSX, go to General settings and set "Show Scroll Bars: Always"
export const SCROLLBARS_ENABLED = _scrollbarDetails.visible;
export const scrollbarOffset = (): number =>
  SCROLLBARS_ENABLED ? SCROLLBAR_WIDTH : 0;

export const queryHas = (key: string): boolean => {
  const updatedQuery = getQuery();
  return has(key)(updatedQuery);
};
export const queryParam = (s: string): unknown => getQuery()[s];

export const DesignerMode = Object.freeze({
  PREVIEW: queryHas('preview') as boolean,
  SNAPSHOT: queryHas('p') as boolean,
  READ_ONLY: queryHas('preview') || (queryHas('p') as boolean),
  ANNOTATIONS:
    queryHas('workflow') &&
    queryParam('workflow') === DESIGNER_WORKFLOWS.COMMENT,
  ANONYMOUS_COMMENT: queryHas('project') as boolean,
});

export const IS_EDITOR: boolean = Boolean(
  // @ts-expect-error - TS2339 - Property 'WebflowEditor' does not exist on type 'Window & typeof globalThis'.
  typeof window !== 'undefined' && window.WebflowEditor
);
export function getApiHost(): null | string {
  return IS_EDITOR && typeof WEBFLOW_API_HOST !== 'undefined'
    ? WEBFLOW_API_HOST
    : null;
}

export {webflowDefaultHost} from './webflow-default-host';
export {getWebflowUrl} from './get-webflow-url';
